import React from 'react';
import PropTypes from 'prop-types';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import {
  Modal,
  ModalBody,
  ModalHeader,
  Label,
  Input,
  Form,
} from 'reactstrap';

import { ReactComponent as CloseButtonSvg } from 'assets/images/location/close-button-svg.svg';

import AppButton from 'components/App/Button';
import AppInputValidation from 'components/App/InputValidation';
import FormField from 'components/AddLocation/FormField';

import { checkHadError } from 'utils';
import { DEPARTMENT_PS, DEPARTMENTS } from 'constants/department';
import { PHONE_REGEX, EMAIL_REGEX, PASSWORD_REGEX } from 'constants/validationHelper';

import styles from './Info.module.scss';

const INITIAL_FORM_VALUES = {
  fullName: '',
  email: '',
  phoneNumber: '',
  username: '',
  password: '',
  department: DEPARTMENT_PS,
  markets: [],
};

const getValidationSchema = (isEdit) => Yup.object({
  fullName: Yup.string()
    .trim()
    .required('Tên không được để trống')
    .min(2, 'Tên phải có ít nhất 2 ký tự')
    .max(100, 'Tên không được quá 100 ký tự'),
  email: isEdit ? Yup.string() : Yup.string()
    .trim()
    .required('Email không được để trống')
    .matches(EMAIL_REGEX, 'Email không hợp lệ')
    .max(100, 'Email không được quá 100 ký tự'),
  phoneNumber: Yup.string()
    .trim()
    .required('Số điện thoại không được để trống')
    .matches(PHONE_REGEX, 'Số điện thoại không hợp lệ'),
  username: isEdit ? Yup.string() : Yup.string()
    .trim()
    .required('Username không được để trống')
    .max(50, 'Username không được quá 50 ký tự'),
  password: isEdit ? Yup.string() : Yup.string()
    .required('Mật khẩu phải dài từ 8 đến 32 ký tự, bao gồm chữ, số và ký tự đặc biệt, không chứa khoảng trắng')
    .min(8, 'Mật khẩu phải dài từ 8 đến 32 ký tự, bao gồm chữ, số và ký tự đặc biệt, không chứa khoảng trắng')
    .max(32, 'Mật khẩu phải dài từ 8 đến 32 ký tự, bao gồm chữ, số và ký tự đặc biệt, không chứa khoảng trắng')
    .matches(
      PASSWORD_REGEX,
      'Mật khẩu phải dài từ 8 đến 32 ký tự, bao gồm chữ, số và ký tự đặc biệt, không chứa khoảng trắng',
    ),
});

function Info(props) {
  const {
    isOpen,
    onToggleModal,
    onNext,
    accountInfo,
    editAccountId,
  } = props;

  const [isShowPassword, setIsShowPassword] = React.useState(true);

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: editAccountId && accountInfo
      ? { ...INITIAL_FORM_VALUES, ...accountInfo }
      : INITIAL_FORM_VALUES,
    validationSchema: getValidationSchema(!!editAccountId),
    onSubmit: (values) => {
      const trimmedValues = Object.keys(values).reduce((acc, key) => {
        const value = values[key];
        acc[key] = typeof value === 'string' ? value.trim() : value;
        return acc;
      }, {});

      onNext(trimmedValues);
    },
  });
  const isPasswordError = checkHadError(validation, 'password');

  const onToggleShowPassword = React.useCallback(() => {
    setIsShowPassword((prev) => !prev);
  }, []);

  const onSelectDepartment = React.useCallback((departmentValue) => {
    validation.setFieldValue('department', departmentValue);
  }, [validation]);

  const onSelectSubRole = React.useCallback((parentValue, roleValue) => {
    if (validation.values.department === roleValue) {
      validation.setFieldValue('department', parentValue);
    } else {
      validation.setFieldValue('department', roleValue);
    }
  }, [validation]);

  const CloseButton = React.useMemo(
    () => (
      <button
        className={styles.ButtonClose}
        onClick={onToggleModal}
        type="button"
      >
        <CloseButtonSvg />
      </button>
    ),
    [onToggleModal],
  );

  React.useEffect(() => {
    if (!isOpen) return;

    validation.resetForm({ values: INITIAL_FORM_VALUES });
    setIsShowPassword(true);
  }, [isOpen]);

  return (
    <Modal
      fade
      size="lg"
      isOpen={isOpen}
      toggle={onToggleModal}
      className={styles.Modal}
    >
      <ModalHeader
        toggle={onToggleModal}
        className={styles.ModalHeader}
        close={CloseButton}
      />

      <ModalBody className={styles.ModalBody}>
        <Form className="form-horizontal" onSubmit={validation.handleSubmit}>
          <Label className="Label">{editAccountId ? 'Chỉnh sửa tài khoản' : 'Thêm tài khoản'}</Label>

          <hr className="Divider" />

          <FormField label="Tên" className="Block">
            <AppInputValidation
              inputClassName={styles.Input}
              name="fullName"
              id="fullName"
              type="text"
              placeholder="Nhập tên"
              validation={validation}
            />
          </FormField>

          <div className="FormRow">
            <FormField label="Email" className="Block">
              <AppInputValidation
                inputClassName={styles.Input}
                name="email"
                id="email"
                type="text"
                placeholder="Nhập Email"
                validation={validation}
                disabled={!!editAccountId}
              />
            </FormField>

            <FormField label="Số điện thoại" className="Block">
              <AppInputValidation
                inputClassName={styles.Input}
                name="phoneNumber"
                id="phoneNumber"
                type="text"
                placeholder="Nhập Số điện thoại"
                validation={validation}
              />
            </FormField>
          </div>

          <div className="FormRow">
            <FormField label="Username" className="Block">
              <AppInputValidation
                inputClassName={styles.Input}
                name="username"
                id="username"
                type="text"
                placeholder="Nhập username"
                validation={validation}
                disabled={!!editAccountId}
              />
            </FormField>

            <FormField label="Mật khẩu" className="Block">
              <div className="PasswordField">
                <AppInputValidation
                  inputClassName={styles.Input}
                  name="password"
                  id="password"
                  type={isShowPassword ? 'password' : 'text'}
                  placeholder="Nhập mật khẩu"
                  validation={validation}
                  // disabled={!!editAccountId}
                />

                <i
                  className={`font-size-18 fas ShowPassIcon ${
                    isPasswordError ? `${styles.ShowPassIconError} eye-error` : 'eye-default'
                  } ${!isShowPassword ? 'fa-eye-slash' : 'fa-eye'}`}
                  aria-hidden="true"
                  onClick={onToggleShowPassword}
                />
              </div>
            </FormField>
          </div>

          <div className="DepartmentSection">
            <span className="DepartmentSection__Label">Bộ phận</span>

            <div className="DepartmentSection__List">
              {DEPARTMENTS.map((department) => {
                const isDepartmentSelected = validation.values.department === department.value ||
                  department.roles?.some((role) => role.value === validation.values.department);

                return (
                  <div className="DepartmentSection__Item" key={department.value}>
                    <label className="DepartmentRadio" htmlFor={`department-${department.value}`}>
                      <Input
                        id={`department-${department.value}`}
                        name="department"
                        type="radio"
                        checked={isDepartmentSelected}
                        onChange={() => onSelectDepartment(department.value)}
                      />
                      <span>{department.label}</span>
                    </label>

                    { department.roles?.length > 0 && (
                      <div className="DepartmentSection__Roles">
                        {department.roles.map((role) => (
                          <label
                            className="DepartmentRadio"
                            htmlFor={`department-role-${role.value}`}
                            key={role.value}
                          >
                            <Input
                              id={`department-role-${role.value}`}
                              name="departmentRole"
                              type="radio"
                              checked={validation.values.department === role.value}
                              onClick={() => onSelectSubRole(department.value, role.value)}
                              onChange={() => {}}
                            />
                            <span>{role.label}</span>
                          </label>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          <hr className="Divider" />

          <div className="Button__Wrapper">
            <AppButton
              classes="Button Button__Cancel"
              type="button"
              onClick={onToggleModal}
            >
              Huỷ
            </AppButton>

            <AppButton classes="Button Button__Next" type="submit">
              Tiếp theo
            </AppButton>
          </div>
        </Form>
      </ModalBody>
    </Modal>
  );
}

Info.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onToggleModal: PropTypes.func.isRequired,
  onNext: PropTypes.func.isRequired,
  accountInfo: PropTypes.instanceOf(Object),
  editAccountId: PropTypes.string,
};

Info.defaultProps = {
  accountInfo: {},
  editAccountId: null,
};

export default React.memo(Info);
